import type { Todo } from "../../domain/entities.ts";

export type InboxTriageReason = "uncollected" | "unplanned";
export interface InboxTriageCandidate { task: Todo; reasons: InboxTriageReason[] }
export interface InboxTriageState { startedAt: number; updatedAt: number; taskIds: string[]; reviewedIds: string[]; completedAt: number | null; deferredIds?: string[]; decisions?: Record<string, string> }

export function inboxTriageCandidates(tasks: Todo[], canEdit: (task: Todo) => boolean): InboxTriageCandidate[] {
  return tasks.flatMap(task => {
    if (task.deleted || task.archivedAt || task.completed || !canEdit(task)) return [];
    const reasons: InboxTriageReason[] = [];
    if (!task.categoryId) reasons.push("uncollected");
    if (!task.dueDate && !task.startDate && !task.startAt && task.priority === "none" && !(task.assignedTo || []).length) reasons.push("unplanned");
    return reasons.length ? [{ task, reasons }] : [];
  }).sort((left, right) => left.task.createdAt - right.task.createdAt || left.task.position - right.task.position || left.task.id.localeCompare(right.task.id));
}

export function normalizeInboxTriage(value: Partial<InboxTriageState> | null | undefined): InboxTriageState | null {
  if (!value || typeof value.startedAt !== "number" || !Number.isFinite(value.startedAt) || !Array.isArray(value.taskIds)) return null;
  const taskIds = [...new Set(value.taskIds.filter(id => typeof id === "string"))];
  const reviewedIds = Array.isArray(value.reviewedIds) ? value.reviewedIds.filter(id => typeof id === "string" && taskIds.includes(id)) : [];
  const deferredIds = Array.isArray(value.deferredIds) ? [...new Set(value.deferredIds.filter(id => typeof id === "string" && taskIds.includes(id) && !reviewedIds.includes(id)))] : [];
  const decisions = value.decisions && typeof value.decisions === "object" ? Object.fromEntries(Object.entries(value.decisions).filter(([id, action]) => taskIds.includes(id) && typeof action === "string")) : {};
  return { startedAt: value.startedAt, updatedAt: value.updatedAt || value.startedAt, taskIds, reviewedIds: [...new Set(reviewedIds)], completedAt: value.completedAt || null, deferredIds, decisions };
}

export function startInboxTriage(candidates: InboxTriageCandidate[], now: number): InboxTriageState {
  return { startedAt: now, updatedAt: now, taskIds: candidates.map(item => item.task.id), reviewedIds: [], completedAt: candidates.length ? null : now };
}

export function advanceInboxTriage(state: InboxTriageState, taskId: string, now: number, action = "keep"): InboxTriageState {
  const reviewedIds = action === "defer" ? state.reviewedIds : [...new Set([...state.reviewedIds, taskId])];
  const deferredIds = [...new Set([...(state.deferredIds || []).filter(id => id !== taskId), ...(action === "defer" ? [taskId] : [])])];
  return { ...state, reviewedIds, deferredIds, decisions: { ...state.decisions, [taskId]: action }, updatedAt: now, completedAt: reviewedIds.length + deferredIds.length >= state.taskIds.length ? now : null };
}

export function currentInboxTriageTask(state: InboxTriageState, availableIds: Set<string>): string | null {
  return state.taskIds.find(id => !state.reviewedIds.includes(id) && !state.deferredIds?.includes(id) && availableIds.has(id)) || null;
}

export function inboxTriageRemaining(state: InboxTriageState | null, candidates: InboxTriageCandidate[]): number {
  const candidateIds = new Set(candidates.map(candidate => candidate.task.id));
  if (!state) return candidateIds.size;
  const snapshotIds = new Set(state.taskIds);
  const newCount = candidates.filter(candidate => !snapshotIds.has(candidate.task.id)).length;
  if (state.completedAt) return newCount;
  return state.taskIds.filter(id => !state.reviewedIds.includes(id) && !state.deferredIds?.includes(id) && candidateIds.has(id)).length + newCount;
}
